import { EditableElement, ConnectorElement } from './types';

/**
 * Command interface for undo/redo pattern
 * Each command must implement execute and undo methods
 */
export interface Command {
  execute(): void;
  undo(): void;
  description?: string;
}

/**
 * Command for adding elements
 */
export class AddElementCommand implements Command {
  description = 'Add element';

  constructor(
    private elements: EditableElement[],
    private element: EditableElement,
    private setElements: (elements: EditableElement[]) => void
  ) {}

  execute(): void {
    this.setElements([...this.elements, this.element]);
  }

  undo(): void {
    this.setElements(this.elements.filter((el) => el.id !== this.element.id));
  }
}

/**
 * Command for deleting elements
 */
export class DeleteElementsCommand implements Command {
  description = 'Delete elements';

  constructor(
    private elements: EditableElement[],
    private deletedIds: string[],
    private setElements: (elements: EditableElement[]) => void
  ) {}

  private deletedElements: EditableElement[] = [];

  execute(): void {
    // Find all elements to be deleted (including the requested ones)
    this.deletedElements = this.elements.filter((el) => this.deletedIds.includes(el.id));
    
    // Find connectors that are connected to the deleted elements
    const connectedConnectorIds: string[] = [];
    this.elements.forEach((el) => {
      if (el.type === 'connector') {
        const connector = el as ConnectorElement;
        const data = connector.data;
        
        // Check if connector's start or end element is being deleted
        if (
          (data.startElementId && this.deletedIds.includes(data.startElementId)) ||
          (data.endElementId && this.deletedIds.includes(data.endElementId))
        ) {
          connectedConnectorIds.push(connector.id);
        }
      }
    });
    
    // Store all IDs to be deleted (original + connectors)
    const allIdsToDelete = [...this.deletedIds, ...connectedConnectorIds];
    
    // Also include connectors in deletedElements for undo
    const deletedConnectors = this.elements.filter((el) => connectedConnectorIds.includes(el.id));
    this.deletedElements = [...this.deletedElements, ...deletedConnectors];
    
    // Delete all elements (original + connected connectors)
    this.setElements(this.elements.filter((el) => !allIdsToDelete.includes(el.id)));
  }

  undo(): void {
    this.setElements([...this.elements, ...this.deletedElements]);
  }
}

/**
 * Command for modifying element properties
 */
export class ModifyElementCommand implements Command {
  description = 'Modify element';

  private oldElement: EditableElement | null = null;

  constructor(
    private elements: EditableElement[],
    private elementId: string,
    private updates: Partial<EditableElement>,
    private setElements: (elements: EditableElement[]) => void
  ) {}

  execute(): void {
    const element = this.elements.find((el) => el.id === this.elementId);
    if (element) {
      this.oldElement = { ...element };
      this.setElements(
        this.elements.map((el) =>
          el.id === this.elementId ? { ...el, ...this.updates } : el
        )
      );
    }
  }

  undo(): void {
    if (this.oldElement) {
      this.setElements(
        this.elements.map((el) => (el.id === this.elementId ? this.oldElement! : el))
      );
    }
  }
}

/**
 * Command for moving elements
 */
export class MoveElementsCommand implements Command {
  description = 'Move elements';

  private oldPositions: Map<string, { x: number; y: number }> = new Map();

  constructor(
    private elements: EditableElement[],
    private updates: Map<string, { x: number; y: number }>,
    private setElements: (elements: EditableElement[]) => void
  ) {}

  execute(): void {
    // Store old positions
    this.updates.forEach((_, id) => {
      const element = this.elements.find((el) => el.id === id);
      if (element) {
        this.oldPositions.set(id, { x: element.x, y: element.y });
      }
    });

    // Apply new positions
    this.setElements(
      this.elements.map((el) => {
        const newPos = this.updates.get(el.id);
        return newPos ? { ...el, ...newPos } : el;
      })
    );
  }

  undo(): void {
    this.setElements(
      this.elements.map((el) => {
        const oldPos = this.oldPositions.get(el.id);
        return oldPos ? { ...el, ...oldPos } : el;
      })
    );
  }
}

/**
 * Command for batch operations
 */
export class BatchCommand implements Command {
  description: string;

  constructor(
    private commands: Command[],
    description?: string
  ) {
    this.description = description || `Batch of ${commands.length} commands`;
  }

  execute(): void {
    this.commands.forEach((cmd) => cmd.execute());
  }

  undo(): void {
    // Undo in reverse order
    [...this.commands].reverse().forEach((cmd) => cmd.undo());
  }
}

/**
 * UndoRedoManager manages command history and provides undo/redo functionality
 */
export class UndoRedoManager {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private maxHistorySize: number;

  constructor(maxHistorySize: number = 50) {
    this.maxHistorySize = maxHistorySize;
  }

  /**
   * Execute a command and add it to history
   */
  execute(command: Command): void {
    command.execute();
    this.undoStack.push(command);
    
    // Limit stack size
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
    
    // Clear redo stack when new command is executed
    this.redoStack = [];
  }

  /**
   * Add a command to history WITHOUT executing it
   * Use when the change has already been applied (e.g., direct Konva manipulation)
   */
  pushWithoutExecute(command: Command): void {
    this.undoStack.push(command);
    
    // Limit stack size
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
    
    // Clear redo stack when new command is added
    this.redoStack = [];
  }

  /**
   * Undo the last command
   */
  undo(): boolean {
    const command = this.undoStack.pop();
    if (command) {
      command.undo();
      this.redoStack.push(command);
      return true;
    }
    return false;
  }

  /**
   * Redo the last undone command
   */
  redo(): boolean {
    const command = this.redoStack.pop();
    if (command) {
      command.execute();
      this.undoStack.push(command);
      return true;
    }
    return false;
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  /**
   * Get undo stack size
   */
  getUndoStackSize(): number {
    return this.undoStack.length;
  }

  /**
   * Get redo stack size
   */
  getRedoStackSize(): number {
    return this.redoStack.length;
  }

  /**
   * Get description of next undo command
   */
  getUndoDescription(): string | undefined {
    const command = this.undoStack[this.undoStack.length - 1];
    return command?.description;
  }

  /**
   * Get description of next redo command
   */
  getRedoDescription(): string | undefined {
    const command = this.redoStack[this.redoStack.length - 1];
    return command?.description;
  }
}
